import React from 'react'
import useGetTeamDetails from '@/services/api/team/useGetTeamDetails'
import useUpdateTeam from '@/services/api/team/useUpdateTeam'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/Card'
import Field from '@/components/ui/Field'
import FieldStatusMessage from '@/components/ui/FieldStatusMessage'
import { Input } from '@/components/ui/Input'
import { Settings } from 'lucide-react'
import { toast } from 'sonner'
import { useController, useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { Label } from '@/components/ui/Label'
import {
  FORECAST_HORIZON_MONTHS,
  FORECAST_HORIZON_WEEKS,
} from '@/data/constants/constants'

const spaceSettingsSchema = z
  .object({
    name: z
      .string()
      .min(1, 'Space name is required')
      .max(100, 'Space name is too long'),
    period_type: z.string().min(1, 'Period type is required'),
    default_horizon: z.number(),
    week_starts_on: z.number().min(0).max(6),
  })
  .refine(
    (data) => {
      if (data.period_type === 'Week') {
        return FORECAST_HORIZON_WEEKS.includes(data.default_horizon)
      } else if (data.period_type === 'Month') {
        return FORECAST_HORIZON_MONTHS.includes(data.default_horizon)
      }
      return false
    },
    {
      message: 'Invalid forecast horizon for selected period type',
      path: ['default_horizon'],
    },
  )

type SpaceSettingsFormData = z.infer<typeof spaceSettingsSchema>

interface SpaceSettingsFormProps {
  teamId: string
  onUpdate?: () => void
}

export default function SpaceSettingsForm({
  teamId,
  onUpdate,
}: SpaceSettingsFormProps) {
  const {
    data: selectedTeam,
    status: teamDetailsStatus,
    promise: teamDetailsPromise,
  } = useGetTeamDetails({
    teamId,
  })

  const {
    action: updateTeam,
    status: { isPending: updateLoading },
  } = useUpdateTeam()

  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
  } = useForm<SpaceSettingsFormData>({
    resolver: zodResolver(spaceSettingsSchema),
    defaultValues: async () => {
      const data = await teamDetailsPromise

      return {
        name: data?.space?.name || '',
        period_type: data?.space?.period_type || '',
        default_horizon: data?.space?.default_horizon || 0,
        week_starts_on: data?.space?.week_starts_on ?? 0,
      }
    },
  })

  const { field: periodTypeField } = useController({
    control: control,
    name: 'period_type',
    shouldUnregister: true,
  })

  const { field: forecastHorizonField } = useController({
    control: control,
    name: 'default_horizon',
    shouldUnregister: true,
  })

  const { field: weekStartsOnField } = useController({
    control: control,
    name: 'week_starts_on',
    shouldUnregister: true,
  })

  const getForecastHorizonOptions = () => {
    if (periodTypeField.value === 'Week') {
      return FORECAST_HORIZON_WEEKS.map((weeks) => ({
        value: weeks,
        label: `${weeks} weeks`,
      }))
    } else if (periodTypeField.value === 'Month') {
      return FORECAST_HORIZON_MONTHS.map((months) => ({
        value: months,
        label: `${months} months`,
      }))
    }
    return []
  }

  const getWeekStartOptions = () => {
    return [
      { value: 1, label: 'Monday' },
      { value: 2, label: 'Tuesday' },
      { value: 3, label: 'Wednesday' },
      { value: 4, label: 'Thursday' },
      { value: 5, label: 'Friday' },
      { value: 6, label: 'Saturday' },
      { value: 0, label: 'Sunday' },
    ]
  }

  const onSubmit = async (data: SpaceSettingsFormData) => {
    await updateTeam({
      teamId: Number(teamId),
      name: data.name,
      period_type: data.period_type,
      default_horizon: data.default_horizon,
      week_starts_on: data.week_starts_on,
    })
  }

  if (teamDetailsStatus === 'pending') {
    return (
      <Card className="w-full p-6">
        <div className="flex items-center justify-center">Loading...</div>
      </Card>
    )
  }

  if (!selectedTeam) {
    return null
  }

  // Check if user has permission to edit
  const hasEditPermission =
    selectedTeam.role === 'Owner' || selectedTeam.role === 'Admin' || 1 === 1

  if (!hasEditPermission) {
    return (
      <Card className="w-full p-6 text-center">
        <Settings className="mx-auto mb-4 h-12 w-12 text-muted-foreground" />
        <h3 className="mb-2">Limited Access</h3>
        <p className="text-muted-foreground">
          You need Owner or Admin permissions to modify space settings.
        </p>
      </Card>
    )
  }

  return (
    <Card className="w-full p-6">
      <div className="mb-4 flex items-center space-x-2">
        <Settings className="h-4 w-4" />
        <h3>Space Configuration</h3>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <Field
          statusText={errors.name?.message}
          status={errors.name ? 'error' : null}
        >
          <Label>Space Name</Label>
          <Input {...register('name')} placeholder="Enter space name" />
          <FieldStatusMessage />
        </Field>

        <div className="flex flex-wrap gap-4">
          <Field
            statusText={errors.period_type?.message}
            status={errors.period_type ? 'error' : null}
            className="min-w-[140px] flex-1"
          >
            <Label>Period Type</Label>
            <Select
              value={periodTypeField.value}
              onValueChange={(value) => periodTypeField.onChange(value)}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select period type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Month">Month</SelectItem>
                <SelectItem value="Week">Week</SelectItem>
              </SelectContent>
            </Select>
            <FieldStatusMessage />
          </Field>

          <Field
            key={periodTypeField.value}
            statusText={errors.default_horizon?.message}
            status={errors.default_horizon ? 'error' : null}
            className="min-w-[160px] flex-1"
          >
            <Label>Default Forecast Horizon</Label>
            <Select
              value={forecastHorizonField.value?.toString()}
              onValueChange={(value) =>
                forecastHorizonField.onChange(Number(value))
              }
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select forecast horizon" />
              </SelectTrigger>
              <SelectContent>
                {getForecastHorizonOptions().map((option) => (
                  <SelectItem key={option.value} value={option.value.toString()}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FieldStatusMessage />
          </Field>

          <Field
            statusText={errors.week_starts_on?.message}
            status={errors.week_starts_on ? 'error' : null}
            className="min-w-[140px] flex-1"
          >
            <Label>Week Starts On</Label>
            <Select
              value={weekStartsOnField.value?.toString()}
              onValueChange={(value) => weekStartsOnField.onChange(Number(value))}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select week start day" />
              </SelectTrigger>
              <SelectContent>
                {getWeekStartOptions().map((option) => (
                  <SelectItem key={option.value} value={option.value.toString()}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FieldStatusMessage />
          </Field>
        </div>

        <div className="flex justify-end">
          <Button type="submit" disabled={updateLoading}>
            {updateLoading ? (
              <>
                <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                Updating...
              </>
            ) : (
              'Update Space Settings'
            )}
          </Button>
        </div>
      </form>
    </Card>
  )
}
